<template>
  <div class="general-tab-content">
    <!-- Basic Information Section -->
    <div class="form-section border rounded p-2 mb-1">
      <b-row>
        <b-col cols="6">
          <!-- Device Number -->

          <validation-provider #default="validationContext" :name="$t('dmp.deviceSetup.deviceNumber')"
            :rules="{ required: true, validDeviceNumber: true }">
            <b-form-group :label="$t('dmp.deviceSetup.deviceNumber')" label-for="deviceNumber">
              <b-form-input id="deviceNumber" v-model="localDeviceForm.deviceNumber" type="number" min="1"
                :state="getValidationState(validationContext)" placeholder="Enter device number"
                :disabled="isEditing" />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Name -->
          <validation-provider #default="validationContext" :name="$t('dmp.name')" :rules="{ required: true, max: 50 }">
            <b-form-group :label="$t('dmp.name')" label-for="deviceName">
              <b-form-input id="deviceName" v-model="localDeviceForm.deviceName" type="text"
                :state="getValidationState(validationContext)" maxlength="50" placeholder="Enter device name"
                class="text-uppercase" />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Device Type -->
          <validation-provider #default="validationContext" :name="$t('dmp.deviceSetup.deviceType')"
            :rules="{ required: true }">
            <b-form-group :label="$t('dmp.deviceSetup.deviceType')" label-for="deviceType">
              <b-form-select id="deviceType" v-model="localDeviceForm.deviceType" :options="deviceTypeOptions"
                :state="getValidationState(validationContext)" />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Device Communication Type -->
        <b-col cols="6">
          <validation-provider #default="validationContext" :name="$t('dmp.deviceSetup.deviceCommunicationType')"
            :rules="{ required: true }">
            <b-form-group :label="$t('dmp.deviceSetup.deviceCommunicationType')" label-for="deviceCommunicationType">
              <b-form-select id="deviceCommunicationType" v-model="localDeviceForm.deviceCommType"
                :options="filteredCommunicationTypeOptions" :state="getValidationState(validationContext)"
                :disabled="!hasNoTypeSelected && !isNoneType && (isCameraType || isZoneExpanderType || isFireType)" />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Serial Number -->
          <validation-provider #default="serialValidationContext" :name="$t('dmp.deviceSetup.serialNumber')"
            :rules="serialNumberRules">
            <b-form-group :label="$t('dmp.deviceSetup.serialNumber')" label-for="serialNumber">
              <b-form-input id="serialNumber" v-model="localDeviceForm.serialNumber"
                :type="isCameraType ? 'number' : 'text'" :min="isCameraType ? 14800000 : null"
                :max="isCameraType ? 14999999 : null" :maxlength="isCameraType ? null : 20"
                placeholder="Enter serial number"
                :state="serialNumberRules ? getValidationState(serialValidationContext) : null"
                :disabled="isSerialNumberDisabled" />
              <b-form-invalid-feedback>
                {{ serialValidationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Supervision Time -->
          <b-form-group :label="$t('dmp.deviceSetup.supervisionTime')" label-for="supervisionTime">
            <b-form-select id="supervisionTime" v-model="localDeviceForm.supervisionTime"
              :options="supervisionTimeOptions" :disabled="isSupervisionTimeDisabled" />
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <!-- Areas Section -->
    <div class="form-section border rounded p-2 mb-1">
      <b-row>
        <!-- Access Areas -->
        <b-col cols="12" md="4">
          <b-form-group :label="$t('dmp.deviceSetup.accessAreas')" label-for="accessAreas">
            <b-form-input id="accessAreas" v-model="localDeviceForm.accessAreas" type="text" maxlength="32"
              :disabled="isAccessAreasDisabled" />
          </b-form-group>
        </b-col>

        <!-- Egress Areas -->
        <b-col cols="12" md="4">
          <b-form-group :label="$t('dmp.deviceSetup.egressAreas')" label-for="egressAreas">
            <b-form-input id="egressAreas" v-model="localDeviceForm.egressAreas" type="text" maxlength="32"
              :disabled="isEgressAreasDisabled" />
          </b-form-group>
        </b-col>

        <!-- Display Areas -->
        <b-col cols="12" md="4">
          <b-form-group :label="$t('dmp.deviceSetup.displayAreas')" label-for="displayAreas">
            <b-form-input id="displayAreas" v-model="localDeviceForm.displayAreas" type="text" maxlength="32"
              :disabled="isDisplayAreasDisabled" />
          </b-form-group>
        </b-col>

        <!-- Strike Time -->
        <b-col cols="12" md="6">
          <b-form-group :label="$t('dmp.deviceSetup.strikeTime')" label-for="strikeTime">
            <b-input-group>
              <b-form-input id="strikeTime" v-model="localDeviceForm.strikeTime" type="number" min="0" max="99"
                :disabled="isStrikeTimeDisabled" />
            </b-input-group>
          </b-form-group>
        </b-col>

        <!-- Strike Delay -->
        <b-col cols="12" md="6">
          <b-form-group :label="$t('dmp.deviceSetup.strikeDelay')" label-for="strikeDelay">
            <b-input-group>
              <b-form-input id="strikeDelay" v-model="localDeviceForm.strikeDelay" type="number" min="0" max="99"
                :disabled="isStrikeDelayDisabled" />
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <!-- Advanced Options Section -->
    <div class="form-section border rounded p-2">
      <b-row>
        <!-- Advanced Options Checkboxes -->
        <b-col cols="12" md="6" lg="4" v-for="option in checkboxOptions" :key="option.key">
          <b-form-group>
            <b-form-checkbox :checked="localDeviceForm[option.key] === 'Y'" @change="updateCheckbox(option.key, $event)"
              :disabled="isAdvancedOptionDisabled(option.key)">
              {{ $t(option.label) }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { computed, watch } from "@vue/composition-api";
import { ValidationProvider, extend } from "vee-validate";

const DOOR_ONLY_NUMBERS = new Set([
  501, 505, 509, 513, 517, 521, 525, 529, 533, 537, 541, 545, 549, 553, 557, 561,
  601, 605, 609, 613, 617, 621, 625, 629, 633, 637, 641, 645, 649, 653, 657, 661,
  701, 705, 709, 713, 717, 721, 725, 729, 733, 737, 741, 745, 749, 753, 757, 761,
  801, 805, 809, 813, 817, 821, 825, 829, 833, 837, 841, 845, 849, 853, 857, 861,
  901, 905, 909, 913, 917, 921, 925, 929, 933, 937, 941, 945, 949, 953, 957, 961,
]);

extend("validDeviceNumber", {
  validate(value) {
    const num = parseInt(value, 10);
    if (isNaN(num)) return false;
    if (num >= 1 && num <= 16) return true;
    if (DOOR_ONLY_NUMBERS.has(num)) return true;
    return false;
  },
  message: "Device number must be 1-16 or a valid door-only number (501-961)",
});

export default {
  name: "GeneralTab",
  components: {
    ValidationProvider,
  },
  props: {
    deviceForm: {
      type: Object,
      required: true,
    },
    selectedDevice: {
      type: Object,
      default: null,
    },
    deviceTypeOptions: {
      type: Array,
      required: true,
    },
    communicationTypeOptions: {
      type: Array,
      required: true,
    },
    supervisionTimeOptions: {
      type: Array,
      required: true,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    isDeleting: {
      type: Boolean,
      default: false,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    getValidationState: {
      type: Function,
      required: true,
    },
  },
  setup(props, { emit }) {
    const localDeviceForm = computed({
      get: () => props.deviceForm,
      set: (value) => emit("update:device-form", value),
    });

    // Determinar si el tipo de dispositivo es DOOR (1)
    const isDoorType = computed(() => localDeviceForm.value.deviceType === "1");

    // Determinar si el tipo de dispositivo es KEYPAD (3)
    const isKeypadType = computed(() => localDeviceForm.value.deviceType === "3");

    // Determinar si el tipo de dispositivo es ZONE_EXPANDER (4)
    const isZoneExpanderType = computed(() => localDeviceForm.value.deviceType === "4");

    // DOORBELL se sustituyó por CAMERA / Video Doorbell (5)
    const isCameraType = computed(() => localDeviceForm.value.deviceType === "5");

    // Determinar si el tipo de dispositivo es FIRE (6)
    const isFireType = computed(() => localDeviceForm.value.deviceType === "6");

    // Determinar si el tipo de dispositivo es NONE (0) - habilita todos los campos
    const isNoneType = computed(() => localDeviceForm.value.deviceType === "0");

    // Sin tipo seleccionado (nuevo dispositivo)
    const hasNoTypeSelected = computed(() => localDeviceForm.value.deviceType === "");

    // Determinar si la comunicación es Wireless (W)
    const isWirelessComm = computed(() => localDeviceForm.value.deviceCommType === "W");

    // Opciones de comunicación filtradas según el tipo de dispositivo
    const filteredCommunicationTypeOptions = computed(() => {
      if (hasNoTypeSelected.value || isNoneType.value) {
        return props.communicationTypeOptions;
      }
      if (isDoorType.value) {
        // DOOR: Keypad Bus (K), Network (N) y Wireless (W)
        return props.communicationTypeOptions.filter(
          (opt) => opt.value === "K" || opt.value === "N" || opt.value === "W"
        );
      } else if (isKeypadType.value) {
        // KEYPAD: Solo Keypad Bus (K) y Wireless (W)
        return props.communicationTypeOptions.filter(
          (opt) => opt.value === "K" || opt.value === "W"
        );
      } else if (isZoneExpanderType.value || isFireType.value) {
        // ZONE EXPANDER / FIRE: Solo Keypad Bus (K)
        return props.communicationTypeOptions.filter((opt) => opt.value === "K");
      }
      // Otros tipos: todas las opciones
      return props.communicationTypeOptions;
    });

    // Serial Number y Supervision Time
    // - DOOR: siempre disabled
    // - KEYPAD: solo enabled si communication type es Wireless
    // - ZONE EXPANDER: siempre disabled
    // - CAMERA: enabled
    const isSerialNumberDisabled = computed(() => {
      if (hasNoTypeSelected.value) return true;
      if (isNoneType.value) {
        return false;
      }
      if (isFireType.value) {
        return true;
      }
      if (isDoorType.value) {
        return !isWirelessComm.value;
      }
      if (isZoneExpanderType.value) {
        return true;
      }
      if (isCameraType.value) {
        return false;
      }
      if (isKeypadType.value) {
        return !isWirelessComm.value;
      }
      return false;
    });

    const isSupervisionTimeDisabled = computed(() => {
      if (hasNoTypeSelected.value) return true;
      if (isNoneType.value) {
        return false;
      }
      if (isFireType.value) {
        return true;
      }
      if (isDoorType.value) {
        return !isWirelessComm.value;
      }
      if (isZoneExpanderType.value) {
        return true;
      }
      if (isCameraType.value) {
        return false;
      }
      if (isKeypadType.value) {
        return !isWirelessComm.value;
      }
      return false;
    });

    // Reglas de deshabilitado por campo
    const isAccessAreasDisabled = computed(() =>
      hasNoTypeSelected.value || (!isNoneType.value && (isZoneExpanderType.value || isCameraType.value || isKeypadType.value || isFireType.value))
    );

    const isEgressAreasDisabled = computed(() =>
      hasNoTypeSelected.value || (!isNoneType.value && (isZoneExpanderType.value || isCameraType.value || isKeypadType.value || isFireType.value))
    );

    const isDisplayAreasDisabled = computed(() =>
      !hasNoTypeSelected.value && !isNoneType.value && (isZoneExpanderType.value || isCameraType.value)
    );

    const isStrikeTimeDisabled = computed(() =>
      hasNoTypeSelected.value || (!isNoneType.value && (isZoneExpanderType.value || isCameraType.value || isKeypadType.value || isFireType.value))
    );

    const isStrikeDelayDisabled = computed(() =>
      hasNoTypeSelected.value || (!isNoneType.value && (isZoneExpanderType.value || isCameraType.value || isKeypadType.value || isFireType.value))
    );

    const isAdvancedOptionDisabled = (optionKey) => {
      if (hasNoTypeSelected.value) {
        // Sin tipo: solo autoForceArm y publicDoor habilitados
        return optionKey !== "autoForceArm" && optionKey !== "publicDoor";
      }
      if (isNoneType.value) {
        return false;
      }
      if (isDoorType.value) {
        return optionKey !== "autoForceArm" &&
          optionKey !== "publicDoor" &&
          optionKey !== "privateDoor" &&
          optionKey !== "fireExit" &&
          optionKey !== "override" &&
          optionKey !== "sendDoorForced" &&
          optionKey !== "doorRealTimeStatus" &&
          optionKey !== "outputGroup";
      }
      if (isCameraType.value) {
        return true;
      }
      if (isFireType.value) {
        return optionKey !== "autoForceArm" && optionKey !== "publicDoor";
      }
      if (isZoneExpanderType.value) {
        return optionKey !== "publicDoor";
      }
      if (isKeypadType.value) {
        return optionKey !== "publicDoor" && optionKey !== "autoForceArm";
      }
      return false;
    };

    const serialNumberRules = computed(() => {
      if (!isCameraType.value) {
        return null;
      }
      return {
        required: true,
        min_value: 14800000,
        max_value: 14999999,
      };
    });

    // Pre-rellenar serial number al cambiar a Wireless
    watch(
      () => localDeviceForm.value.deviceCommType,
      (newCommType) => {
        if (newCommType === "W" && !localDeviceForm.value.serialNumber) {
          emit("update:device-form", {
            ...localDeviceForm.value,
            serialNumber: "14",
          });
        }
      }
    );

    // Defaults/normalización al cambiar tipo
    watch(
      () => localDeviceForm.value.deviceType,
      (newType) => {
        // ZONE EXPANDER / FIRE: comm type por default KEYPAD_BUS
        if ((newType === "4" || newType === "6") && localDeviceForm.value.deviceCommType !== "K") {
          emit("update:device-form", {
            ...localDeviceForm.value,
            deviceCommType: "K",
          });
          return;
        }

        // KEYPAD: solo K/W. Si quedó N, volver a K.
        if (newType === "3") {
          const comm = localDeviceForm.value.deviceCommType;
          if (comm !== "K" && comm !== "W") {
            emit("update:device-form", {
              ...localDeviceForm.value,
              deviceCommType: "K",
            });
          }
        }
      }
    );

    const updateCheckbox = (key, value) => {
      emit("update:device-form", {
        ...localDeviceForm.value,
        [key]: value ? "Y" : "N",
      });
    };

    const checkboxOptions = computed(() => [
      {
        key: "fireExit",
        label: "dmp.deviceSetup.fireExit",
      },
      {
        key: "autoForceArm",
        label: "dmp.deviceSetup.autoForceArm",
      },
      {
        key: "publicDoor",
        label: "dmp.deviceSetup.publicDoor",
      },
      {
        key: "outputGroup",
        label: "dmp.deviceSetup.outputGroup",
      },
      {
        key: "doorRealTimeStatus",
        label: "Door Real-time Status",
      },
      {
        key: "privateDoor",
        label: "dmp.deviceSetup.privateDoor",
      },
      {
        key: "override",
        label: "dmp.deviceSetup.override",
      },
      {
        key: "sendDoorForced",
        label: "dmp.deviceSetup.sendDoorForcedMessage",
      },
    ]);

    return {
      localDeviceForm,
      checkboxOptions,
      updateCheckbox,
      isDoorType,
      isKeypadType,
      isZoneExpanderType,
      isCameraType,
      isFireType,
      isNoneType,
      hasNoTypeSelected,
      isWirelessComm,
      filteredCommunicationTypeOptions,
      isSerialNumberDisabled,
      isSupervisionTimeDisabled,
      isAccessAreasDisabled,
      isEgressAreasDisabled,
      isDisplayAreasDisabled,
      isStrikeTimeDisabled,
      isStrikeDelayDisabled,
      isAdvancedOptionDisabled,
      serialNumberRules,
    };
  },
};
</script>

<style scoped lang="scss">
.form-section {
  border: 1px solid #ebe9f1;

  &:last-child {
    margin-bottom: 0;
  }
}

// Dark mode adjustments
.dark-layout {
  .form-section {
    border-color: #404656;
  }
}
</style>
